import { Heading } from "@solomon-labs/design/components/ui";
import React from "react";

import { cn } from "../../utils/cn";

export interface SectionProps {
    /**
     * The section title. Rendered via `Heading` with `titleVariant="h2"`
     * — the dense console section-header scale (ABC Social bold 20px),
     * per yaas-styles.
     */
    heading: React.ReactNode;
    /**
     * Optional right-aligned control in the header row — e.g. a
     * "+Create Policy" button. Kept as a slot so this generic shell never
     * imports a domain control (dependency firewall). When present the
     * header row becomes a `justify-between` flex row.
     */
    action?: React.ReactNode;
    /**
     * Optional copy rendered below the title, inside the header block —
     * e.g. the Funding section's "Select the account(s)…" line. A slot so
     * the shell owns no domain copy.
     */
    subtitle?: React.ReactNode;
    /** The section body. */
    children?: React.ReactNode;
    /**
     * Extend / override the outer `<section>` chrome — typically the
     * vertical rhythm (`pt-8`, `mt-8`, …). yaas-styles: wrappers carry
     * only `py-*` / `pt-*` / `mt-*`, never `px-*` (dividers run
     * edge-to-edge; padding lives on children).
     */
    className?: string;
    /**
     * Extend / override the header block chrome — its horizontal padding
     * and any bottom margin before the body (`px-4`, `px-4 mb-6`, …).
     * Padding lives here (not on `<section>`) so a section divider would
     * run edge-to-edge.
     */
    headerClassName?: string;
    /**
     * When true, render a horizontal rule under the header block as part of
     * the heading (separating it from the body). Runs edge-to-edge — the
     * header's horizontal padding sits inside the block, the border spans its
     * full width.
     */
    divider?: boolean;
}

/**
 * Section — a generic in-page section shell: a header row (an `h2` title +
 * an optional right-aligned `action` slot, with an optional `subtitle`
 * below) over a `children` body slot. It standardizes the section-header
 * row and its spacing only; it knows nothing about modes (create vs read) —
 * the shell is dumb, the caller supplies the body.
 *
 * Generic Component: it knows no Solomon/YaaS entity. The title is a prop;
 * the action and body are slots, so this shell never imports a Domain
 * Component (dependency firewall). Composes the shared `Heading` UI Element
 * (`titleVariant="h2"`) at the top — Section (GC) = Heading (UI Element) +
 * body slot — mirroring how `PageHeader` composes `Heading`.
 *
 * Padding rule (yaas-styles): the `<section>` wrapper carries only vertical
 * rhythm (`className`); horizontal padding lives on the header block
 * (`headerClassName`) and on body children, so dividers run edge-to-edge.
 *
 * @version 1.1.0
 * @published 2026-07-04
 * @history
 *   1.0.0 — Initial publish. Introduced to wrap the client Programs
 *           ProgramDetail Configuration / Policies / Funding section
 *           headers as a domain-free GC. Visual no-op vs the inline
 *           `<section><div className="px-4"><Text variant="h2">…` headers
 *           it replaces.
 *   1.1.0 — Header row now composes the shared `Heading` UI Element
 *           (`titleVariant="h2"`, `asElement="header"`) instead of a
 *           hand-rolled `<Text variant="h2">` + action/subtitle layout;
 *           visual no-op (`items-center` override on the action row,
 *           `gap-2` override on the subtitle stack preserve the prior
 *           spacing). Funding's subtitle call site now passes a plain
 *           string (Heading styles it) to avoid double-wrapping `Text`.
 *
 * @see Section.intent.md   policy + recipes
 * @see Section.layout.html intended layout
 */
export function Section({
    heading,
    action,
    subtitle,
    children,
    className,
    headerClassName = "px-4",
    divider = false
}: SectionProps): React.ReactElement {
    const hasAction = action !== undefined && action !== null;
    const hasSubtitle = subtitle !== undefined && subtitle !== null;

    return (
        <section className={className}>
            {/* Section (GC) = Heading (UI Element) + body slot. Heading owns
                the title (h2), the optional right-aligned action, and the
                optional subtitle stacked below. headerClassName carries the
                horizontal padding (yaas-styles: dividers run edge-to-edge).
                Class overrides preserve the pre-composition spacing:
                - action row: `items-center` overrides Heading's default
                  `items-baseline` (tailwind-merge).
                - subtitle stack: `gap-2` (8px) overrides Heading's `gap-1`
                  (4px) to match the prior `mt-2` title↔subtitle gap. */}
            <Heading
                title={heading}
                titleVariant="h2"
                actions={action}
                subtitle={subtitle}
                asElement="header"
                className={cn(
                    hasAction && "items-center",
                    !hasAction && hasSubtitle && "gap-2",
                    // Divider as part of the heading: space below the title,
                    // then an edge-to-edge rule before the body.
                    divider && "pb-4 border-b border-line-base",
                    headerClassName
                )}
            />
            {children}
        </section>
    );
}
